import React from "react";
import PropTypes from "prop-types";
//Material imports
import Button from "@mui/material/Button";
import withStyles from "@mui/styles/withStyles";
import Typography from "@mui/material/Typography";
//Limitless imports
import { withi18n } from "@limitless/customer/components";
import { locale as translatableLocale } from "@limitless/common/utils";
import {
    SplitScreenContainer,
    AnimateList,
    AnnouncementCard,
    AnnouncementContent,
    LoadingSpinner
} from "@limitless/portal";
import { useExpertAnnouncementsContext, useExpertTaskListContext, useExpertProfileContext } from "@limitless/expert";
import ExpertRemoteContent from "../components/expert-remote-content/ExpertRemoteContent";
import { usePageViewRecorder } from "@limitless/analytics";
import NoAnnouncementsScreen from "./NoAnnouncementsScreen";

const AnnouncementsScreen = ({ classes, t }) => {
    const {
        selectedAnnouncement,
        selectAnnouncement,
        getLowPriorityAnnouncements,
        acknowledgeAnnouncement,
        loadUserAnnouncements
    } = useExpertAnnouncementsContext();
    const {
        tasklist: { loading }
    } = useExpertTaskListContext();
    const { formatDate } = useExpertProfileContext();
    const locale = React.useMemo(() => translatableLocale(t, true), []);
    usePageViewRecorder("Announcements");

    const lowPriorityAnnouncements = getLowPriorityAnnouncements();

    React.useEffect(() => {
        loadUserAnnouncements();
    }, []);

    const handleAnnouncementsClick = announcement => {
        selectAnnouncement(announcement);
    };

    const renderListItem = (announcement, index) => {
        return (
            <Button
                id={`announcementButton${index}`}
                classes={{ root: classes.buttonRoot }}
                onClick={() => handleAnnouncementsClick(announcement)}
                disableFocusRipple
            >
                <AnnouncementCard
                    title={announcement.summary}
                    date={formatDate(new Date(announcement.created), locale)}
                    imageSource={announcement.imageSource}
                    backgroundColor={announcement.backgroundColor}
                    state={announcement.state}
                />
            </Button>
        );
    };

    return (
        <SplitScreenContainer title="">
            <div className={classes.announcementContainer}>
                {loading && <LoadingSpinner />}
                {/* will render the list with an incremental animation */}
                {!loading && lowPriorityAnnouncements.length !== 0 && (
                    <AnimateList items={lowPriorityAnnouncements} renderListItem={renderListItem} />
                )}
                {!loading && lowPriorityAnnouncements.length === 0 && <NoAnnouncementsScreen t={t} classes={classes} />}
            </div>
            {!loading && selectedAnnouncement && (
                <div>
                    <AnnouncementContent
                        announcement={selectedAnnouncement}
                        onDismiss={acknowledgeAnnouncement}
                        remoteContentComponent={ExpertRemoteContent}
                        title={selectedAnnouncement.summary}
                        date={formatDate(new Date(selectedAnnouncement.created), locale)}
                    />
                </div>
            )}
        </SplitScreenContainer>
    );
};

const styles = theme => ({
    announcementContainer: {
        display: "flex",
        flexDirection: "column",
        paddingRight: 40,
        paddingLeft: 20,
        paddingTop: 20
    },
    buttonRoot: {
        padding: 0,
        borderRadius: 0,
        marginBottom: 30,
        width: "100%",
        "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.03)"
        }
    },
    noAnnouncement: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "70vh"
    }
});

export default withStyles(styles)(withi18n(AnnouncementsScreen));

AnnouncementsScreen.propTypes = {
    classes: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired
};
